<template>
  <div class="dashboard-inteligente min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50 p-6">
    <!-- Header Espectacular -->
    <div class="mb-8">
      <div class="flex items-center justify-between mb-4">
        <div>
          <h1 class="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 mb-2">
            🤖 Dashboard Inteligente
          </h1>
          <p class="text-gray-600 text-lg">Análisis predictivo con IA • Sistema MARBEL</p>
        </div>
        <div class="flex gap-3">
          <button
            @click="cargarDatos"
            :disabled="cargando"
            class="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
          >
            <svg v-if="!cargando" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <div v-else class="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            {{ cargando ? 'Analizando...' : 'Actualizar' }}
          </button>
        </div>
      </div>

      <!-- Filtros de Fecha -->
      <div class="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-2">📅 Fecha Inicio</label>
            <input
              type="date"
              v-model="filtros.fechaInicio"
              class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            />
          </div>
          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-2">📅 Fecha Fin</label>
            <input
              type="date"
              v-model="filtros.fechaFin"
              class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            />
          </div>
          <div class="flex items-end">
            <button
              @click="aplicarFiltros"
              class="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
            >
              🔍 Consultar
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="cargando" class="flex justify-center items-center py-20">
      <div class="text-center">
        <div class="relative">
          <div class="animate-spin rounded-full h-24 w-24 border-8 border-gray-200 border-t-blue-600 mx-auto mb-6"></div>
          <div class="absolute inset-0 flex items-center justify-center">
            <span class="text-3xl">🤖</span>
          </div>
        </div>
        <p class="text-xl font-semibold text-gray-700 mb-2">Analizando datos con IA...</p>
        <p class="text-gray-500">Procesando {{ metadata?.cobertura_datos?.total_registros_analizados || 0 }} registros</p>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="bg-gradient-to-r from-red-50 to-pink-50 border-2 border-red-200 rounded-2xl p-8 shadow-lg">
      <div class="flex items-center gap-4">
        <div class="bg-red-100 rounded-full p-4">
          <svg class="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div>
          <h3 class="text-xl font-bold text-red-800 mb-1">Error al cargar el dashboard</h3>
          <p class="text-red-600">{{ error }}</p>
        </div>
      </div>
    </div>

    <!-- Dashboard Content -->
    <div v-else-if="dashboardData">
      <!-- Score de Salud del Negocio - HERO SECTION -->
      <div class="mb-8">
        <div class="bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 rounded-3xl shadow-2xl p-8 text-white relative overflow-hidden">
          <div class="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full -mr-32 -mt-32"></div>
          <div class="absolute bottom-0 left-0 w-96 h-96 bg-white opacity-5 rounded-full -ml-48 -mb-48"></div>
          
          <div class="relative z-10">
            <div class="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
              <!-- Score Circle -->
              <div class="flex justify-center">
                <div class="relative">
                  <svg class="transform -rotate-90 w-48 h-48">
                    <circle cx="96" cy="96" r="88" stroke="rgba(255,255,255,0.2)" stroke-width="12" fill="none" />
                    <circle
                      cx="96"
                      cy="96"
                      r="88"
                      stroke="white"
                      stroke-width="12"
                      fill="none"
                      :stroke-dasharray="552"
                      :stroke-dashoffset="552 - (552 * scoreGeneral / 100)"
                      class="transition-all duration-1000"
                    />
                  </svg>
                  <div class="absolute inset-0 flex items-center justify-center flex-col">
                    <span class="text-5xl font-black">{{ scoreGeneral }}</span>
                    <span class="text-sm font-semibold opacity-90">/ 100</span>
                  </div>
                </div>
              </div>

              <!-- Info -->
              <div class="lg:col-span-2">
                <div class="flex items-center gap-3 mb-4">
                  <span class="text-6xl">{{ getEstadoEmoji(clasificacion) }}</span>
                  <div>
                    <h2 class="text-4xl font-black mb-1">{{ clasificacion }}</h2>
                    <p class="text-xl opacity-90">Salud del Negocio</p>
                  </div>
                </div>
                <p class="text-lg opacity-95 mb-6">{{ mensajeSalud }}</p>
                
                <!-- Mini Scores -->
                <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div v-for="(detail, key) in scoresDetallados" :key="key" class="bg-white bg-opacity-20 rounded-xl p-4 backdrop-blur-sm">
                    <div class="text-2xl font-bold">{{ detail.score }}</div>
                    <div class="text-sm opacity-90 capitalize">{{ key }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Alertas Críticas -->
      <div v-if="alertasCriticas.length > 0" class="mb-8">
        <h3 class="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
          🚨 Alertas Críticas
        </h3>
        <div class="grid grid-cols-1 gap-4">
          <div
            v-for="(alerta, index) in alertasCriticas"
            :key="index"
            :class="[
              'rounded-2xl p-6 shadow-lg border-l-4 transition-all hover:shadow-xl',
              alerta.tipo === 'CRITICA' ? 'bg-red-50 border-red-500' : 'bg-yellow-50 border-yellow-500'
            ]"
          >
            <div class="flex items-start gap-4">
              <div :class="[
                'rounded-full p-3',
                alerta.tipo === 'CRITICA' ? 'bg-red-100' : 'bg-yellow-100'
              ]">
                <svg class="w-6 h-6" :class="alerta.tipo === 'CRITICA' ? 'text-red-600' : 'text-yellow-600'" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div class="flex-1">
                <div class="flex items-center justify-between mb-2">
                  <h4 :class="['text-lg font-bold', alerta.tipo === 'CRITICA' ? 'text-red-800' : 'text-yellow-800']">
                    {{ alerta.titulo }}
                  </h4>
                  <span :class="[
                    'px-3 py-1 rounded-full text-xs font-semibold',
                    alerta.tipo === 'CRITICA' ? 'bg-red-200 text-red-800' : 'bg-yellow-200 text-yellow-800'
                  ]">
                    {{ alerta.categoria }}
                  </span>
                </div>
                <p :class="['mb-3', alerta.tipo === 'CRITICA' ? 'text-red-700' : 'text-yellow-700']">{{ alerta.mensaje }}</p>
                <div :class="['text-sm font-semibold', alerta.tipo === 'CRITICA' ? 'text-red-900' : 'text-yellow-900']">
                  💡 Acción: {{ alerta.accion_requerida }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Métricas Clave -->
      <div class="mb-8">
        <h3 class="text-2xl font-bold text-gray-800 mb-4">📊 Métricas Clave</h3>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div class="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl shadow-xl p-6 text-white transform hover:scale-105 transition-all duration-300">
            <div class="flex items-center justify-between mb-4">
              <div class="bg-white bg-opacity-20 rounded-xl p-3">
                <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <span class="text-sm font-semibold bg-white bg-opacity-20 px-3 py-1 rounded-full">
                +{{ kpisVentas.variacion_porcentual }}%
              </span>
            </div>
            <div class="text-3xl font-black mb-1">${{ formatNumber(metricasClave.ingresos_totales) }}</div>
            <div class="text-blue-100 text-sm font-medium">Ingresos Totales</div>
          </div>

          <div class="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl shadow-xl p-6 text-white transform hover:scale-105 transition-all duration-300">
            <div class="flex items-center justify-between mb-4">
              <div class="bg-white bg-opacity-20 rounded-xl p-3">
                <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <span class="text-sm font-semibold bg-white bg-opacity-20 px-3 py-1 rounded-full">
                {{ kpisConversion.valor_actual }}%
              </span>
            </div>
            <div class="text-3xl font-black mb-1">{{ metricasClave.total_ventas }}</div>
            <div class="text-green-100 text-sm font-medium">Ventas Cerradas</div>
          </div>

          <div class="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl shadow-xl p-6 text-white transform hover:scale-105 transition-all duration-300">
            <div class="flex items-center justify-between mb-4">
              <div class="bg-white bg-opacity-20 rounded-xl p-3">
                <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
            </div>
            <div class="text-3xl font-black mb-1">{{ metricasClave.clientes_unicos }}</div>
            <div class="text-purple-100 text-sm font-medium">Clientes Únicos</div>
          </div>

          <div class="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl shadow-xl p-6 text-white transform hover:scale-105 transition-all duration-300">
            <div class="flex items-center justify-between mb-4">
              <div class="bg-white bg-opacity-20 rounded-xl p-3">
                <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                </svg>
              </div>
            </div>
            <div class="text-3xl font-black mb-1">${{ formatNumber(metricasClave.ticket_promedio) }}</div>
            <div class="text-orange-100 text-sm font-medium">Ticket Promedio</div>
          </div>
        </div>
      </div>

      <!-- Tabs de Contenido -->
      <div class="bg-white rounded-2xl shadow-xl border border-gray-100">
        <div class="border-b border-gray-200">
          <nav class="flex overflow-x-auto">
            <button
              v-for="tab in tabs"
              :key="tab.id"
              @click="tabActivo = tab.id"
              :class="[
                'py-4 px-6 text-sm font-semibold border-b-2 transition-all whitespace-nowrap',
                tabActivo === tab.id
                  ? 'border-blue-500 text-blue-600 bg-blue-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              ]"
            >
              {{ tab.icon }} {{ tab.nombre }}
            </button>
          </nav>
        </div>

        <div class="p-6">
          <!-- Tab: Embudo de Ventas -->
          <div v-if="tabActivo === 'embudo'">
            <EmbudoVentas :data="embudoData" />
          </div>

          <!-- Tab: Recomendaciones IA -->
          <div v-if="tabActivo === 'recomendaciones'">
            <RecomendacionesIA :recomendaciones="recomendaciones" />
          </div>

          <!-- Tab: Vendedores -->
          <div v-if="tabActivo === 'vendedores'">
            <AnalisisVendedores :vendedores="vendedores" />
          </div>

          <!-- Tab: Predicciones -->
          <div v-if="tabActivo === 'predicciones'">
            <PrediccionesIA :predicciones="predicciones" />
          </div>

          <!-- Tab: Patrones Ocultos -->
          <div v-if="tabActivo === 'patrones'">
            <PatronesOcultos :patrones="patronesOcultos" />
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="text-center py-20">
      <div class="text-6xl mb-4">🤖</div>
      <h3 class="text-2xl font-bold text-gray-700 mb-2">Dashboard Inteligente</h3>
      <p class="text-gray-500 mb-6">Haz clic en "Actualizar" para cargar los datos con IA</p>
    </div>
  </div>
</template>

<script>
import EmbudoVentas from './components/EmbudoVentas.vue'
import RecomendacionesIA from './components/RecomendacionesIA.vue'
import AnalisisVendedores from './components/AnalisisVendedores.vue'
import PrediccionesIA from './components/PrediccionesIA.vue'
import PatronesOcultos from './components/PatronesOcultos.vue'
import dashboardIAService from '@/Services/dashboardIA'

export default {
  name: 'DashboardInteligente',
  components: {
    EmbudoVentas,
    RecomendacionesIA,
    AnalisisVendedores,
    PrediccionesIA,
    PatronesOcultos
  },
  data() {
    return {
      cargando: false,
      error: null,
      dashboardData: null,
      tabActivo: 'embudo',
      filtros: {
        fechaInicio: this.getPrimerDiaMes(),
        fechaFin: this.getFechaActual()
      },
      tabs: [
        { id: 'embudo', nombre: 'Embudo de Ventas', icon: '📊' },
        { id: 'recomendaciones', nombre: 'Recomendaciones IA', icon: '🤖' },
        { id: 'vendedores', nombre: 'Vendedores', icon: '👥' },
        { id: 'predicciones', nombre: 'Predicciones', icon: '🔮' },
        { id: 'patrones', nombre: 'Patrones Ocultos', icon: '🔍' }
      ]
    }
  },
  computed: {
    metadata() {
      return this.dashboardData?.metadata || null
    },
    scoreGeneral() {
      return this.dashboardData?.score_salud_negocio?.score_general || 0
    },
    clasificacion() {
      return this.dashboardData?.score_salud_negocio?.clasificacion || 'N/A'
    },
    mensajeSalud() {
      return this.dashboardData?.score_salud_negocio?.mensaje || ''
    },
    scoresDetallados() {
      return this.dashboardData?.score_salud_negocio?.scores_detallados || {}
    },
    metricasClave() {
      return this.dashboardData?.resumen_ejecutivo?.metricas_clave || {}
    },
    kpisVentas() {
      return this.dashboardData?.kpis_inteligentes?.ventas || {}
    },
    kpisConversion() {
      return this.dashboardData?.kpis_inteligentes?.conversion || {}
    },
    alertasCriticas() {
      const alertas = this.dashboardData?.alertas_inteligentes?.alertas || []
      return alertas.filter(a => a.tipo === 'CRITICA' || a.tipo === 'ADVERTENCIA').slice(0, 3)
    },
    embudoData() {
      return this.dashboardData?.flujo_completo_negocio || null
    },
    recomendaciones() {
      return this.dashboardData?.recomendaciones_ia || []
    },
    vendedores() {
      return this.dashboardData?.inteligencia_vendedores || null
    },
    predicciones() {
      return this.dashboardData?.predicciones_ia || null
    },
    patronesOcultos() {
      return this.dashboardData?.patrones_ocultos || null
    }
  },
  mounted() {
    this.cargarDatos()
  },
  methods: {
    async cargarDatos() {
      this.cargando = true
      this.error = null
      
      try {
        const response = await dashboardIAService.GET_DASHBOARD_INTELIGENTE({
          fechaInicio: this.filtros.fechaInicio,
          fechaFin: this.filtros.fechaFin,
          predicciones: 'true',
          ia: 'true'
        })
        
        if (response.error) {
          throw new Error(response.error)
        }
        
        this.dashboardData = response
      } catch (err) {
        this.error = err.message || 'Error al cargar los datos del dashboard'
        console.error('Error:', err)
      } finally {
        this.cargando = false
      }
    },
    aplicarFiltros() {
      this.cargarDatos()
    },
    formatNumber(num) {
      if (!num) return '0'
      return new Intl.NumberFormat('es-MX').format(num)
    },
    getEstadoEmoji(estado) {
      const emojis = {
        'EXCELENTE': '🌟',
        'BUENO': '✅',
        'REGULAR': '⚠️',
        'CRITICO': '🚨'
      }
      return emojis[estado] || '📊'
    },
    getPrimerDiaMes() {
      const fecha = new Date()
      return new Date(fecha.getFullYear(), fecha.getMonth(), 1).toISOString().split('T')[0]
    },
    getFechaActual() {
      return new Date().toISOString().split('T')[0]
    }
  }
}
</script>

<style scoped>
.dashboard-inteligente {
  animation: fadeIn 0.5s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
